import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Globe, MapPin, Factory, ArrowUpRight, Search, X } from "lucide-react";

const SITE = "https://spa-report-hub.emergent.host";

export default function Manufacturers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ countries: [], positionings: [], groups: [] });
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get("q") || "");

  const activeCountry = searchParams.get("country") || "";
  const activePos = searchParams.get("positioning") || "";
  const activeEurope = searchParams.get("europe") || "";
  const activeQ = searchParams.get("q") || "";

  useSEO({
    title: "Annuaire mondial des fabricants de spas — DoctoSpa",
    description:
      "103 fabricants de spas référencés : USA, Canada, Chine, Europe. Identifiez le vrai fabricant derrière chaque marque, positionnement, présence Europe, OEM.",
    url: `${SITE}/annuaire-fabricants`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Annuaire mondial des fabricants de spas",
      url: `${SITE}/annuaire-fabricants`,
    },
  });

  useEffect(() => {
    api.get("/manufacturers/facets").then((r) => setFacets(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (activeQ) params.q = activeQ;
    if (activeCountry) params.country = activeCountry;
    if (activePos) params.positioning = activePos;
    if (activeEurope) params.europe = activeEurope;
    params.limit = 200;
    api
      .get("/manufacturers/", { params })
      .then((r) => {
        setItems(r.data.manufacturers || []);
        setTotal(r.data.total || 0);
      })
      .finally(() => setLoading(false));
  }, [activeQ, activeCountry, activePos, activeEurope]);

  const updateFilter = (key, value) => {
    const params = Object.fromEntries(searchParams);
    if (value) params[key] = value;
    else delete params[key];
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearchParams({});
  };

  const submitSearch = (e) => {
    e.preventDefault();
    updateFilter("q", searchInput.trim());
  };

  const hasFilters = activeQ || activeCountry || activePos || activeEurope;

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14 lg:py-20" data-testid="manufacturers-page">
      <div className="max-w-3xl">
        <div className="eyebrow">Annuaire mondial</div>
        <h1 className="mt-3 font-display text-4xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
          <span className="text-primary">103</span> fabricants de spas<br />référencés.
        </h1>
        <p className="mt-5 text-muted-foreground leading-relaxed">
          Qui fabrique réellement votre spa ? Groupe industriel, pays d'origine, positionnement,
          présence en Europe, statut OEM. Une source unique pour comprendre le marché.
        </p>
      </div>

      {/* Search bar */}
      <form onSubmit={submitSearch} className="mt-10 flex gap-2 max-w-xl" data-testid="manufacturers-search-form">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Rechercher : Jacuzzi, Canada, Balboa..."
            className="pl-10 h-11"
            data-testid="manufacturers-search-input"
          />
        </div>
        <Button type="submit" className="h-11 px-6 rounded-md" data-testid="manufacturers-search-btn">
          Rechercher
        </Button>
      </form>

      {/* Filter chips */}
      <div className="mt-8 flex flex-wrap gap-2" data-testid="manufacturers-filters">
        <FilterGroup label="Pays" items={facets.countries.slice(0, 10)} active={activeCountry} onChange={(v) => updateFilter("country", v)} testidPrefix="filter-country" />
        <FilterGroup label="Positionnement" items={facets.positionings} active={activePos} onChange={(v) => updateFilter("positioning", v)} testidPrefix="filter-positioning" />
        <FilterGroup label="Europe" items={[{ value: "Oui", count: null }, { value: "Non", count: null }, { value: "Limité", count: null }]} active={activeEurope} onChange={(v) => updateFilter("europe", v)} testidPrefix="filter-europe" />
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="text-xs font-semibold px-3 py-1.5 rounded-full bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors inline-flex items-center gap-1"
            data-testid="manufacturers-clear-filters"
          >
            <X className="h-3 w-3" /> Effacer les filtres
          </button>
        )}
      </div>

      {/* Results */}
      <div className="mt-8 text-sm text-muted-foreground" data-testid="manufacturers-count">
        {loading ? "Chargement…" : `${total} fabricant${total > 1 ? "s" : ""} trouvé${total > 1 ? "s" : ""}`}
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="manufacturers-grid">
        {items.map((m) => (
          <ManufacturerCard key={m.id} m={m} />
        ))}
      </div>

      {!loading && items.length === 0 && (
        <div className="mt-16 text-center text-sm text-muted-foreground">
          Aucun fabricant ne correspond à ces critères.
        </div>
      )}
    </div>
  );
}

function FilterGroup({ label, items, active, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5 items-center" data-testid={`${testidPrefix}-group`}>
      <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mr-1">{label}:</span>
      {items.map((it) => (
        <button
          key={it.value}
          onClick={() => onChange(active === it.value ? "" : it.value)}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
            active === it.value ? "bg-secondary text-white" : "bg-black/5 hover:bg-black/10 text-foreground"
          }`}
          data-testid={`${testidPrefix}-${it.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          {it.value}
          {it.count !== null && it.count !== undefined && <span className="opacity-60"> ({it.count})</span>}
        </button>
      ))}
    </div>
  );
}

function ManufacturerCard({ m }) {
  const posColor =
    m.positioning?.toLowerCase().includes("luxe")
      ? "bg-purple-100 text-purple-800"
      : m.positioning?.toLowerCase().includes("premium")
      ? "bg-emerald-100 text-emerald-800"
      : m.positioning?.toLowerCase().includes("oem")
      ? "bg-amber-100 text-amber-800"
      : m.positioning?.toLowerCase().includes("entrée") || m.positioning?.toLowerCase().includes("entree")
      ? "bg-slate-100 text-slate-700"
      : "bg-blue-100 text-blue-800";

  return (
    <Link to={`/annuaire-fabricants/${m.slug}`} className="group" data-testid={`manufacturer-card-${m.slug}`}>
      <Card className="h-full border-black/10 hover:border-primary/40 hover:shadow-lg transition-all p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-base leading-tight group-hover:text-primary transition-colors truncate">
              {m.name}
            </h3>
            {m.group_name && m.group_name !== m.name && (
              <p className="mt-0.5 text-xs text-muted-foreground truncate">Groupe : {m.group_name}</p>
            )}
          </div>
          <ArrowUpRight className="h-4 w-4 text-muted-foreground shrink-0" />
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {m.positioning && (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${posColor}`}>
              {m.positioning}
            </span>
          )}
          {m.oem?.toLowerCase().startsWith("oui") && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
              OEM
            </span>
          )}
          {m.europe_presence === "Oui" && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-black/5 text-foreground">
              🇪🇺 Europe
            </span>
          )}
        </div>

        <div className="mt-4 space-y-1.5 text-xs text-muted-foreground">
          {m.country && (
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate">{m.country}</span>
            </div>
          )}
          {m.products && (
            <div className="flex items-start gap-1.5">
              <Factory className="h-3 w-3 shrink-0 mt-0.5" />
              <span className="line-clamp-2">{m.products}</span>
            </div>
          )}
        </div>
      </Card>
    </Link>
  );
}
